import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ContainerWrapper from "../../components/ContainerWrapper/ContainerWrapper";
import ActionButton from "../../components/Button/ActionButton";
import Loading from "../../components/Loading/Loading";
import HelpButton from "../../components/payments/HelpContent/HelpButton";
import AddPaymentLineDialog from "./AddPaymentLineDialog";
import PaymentDetailsSection from "./PaymentDetailsSection";
import PaymentLinesSection from "./PaymentLinesSection";
import PaymentRequestedDialog from "./PaymentRequestedDialog";
import RemoveLineDialog from "./RemoveLineDialog";
import RequestPaymentMessage from "./RequestPaymentMessage";
import usePaymentConfigurations from "../../hooks/usePaymentConfigurations";
import useProfessionalAccountStatus from "../../hooks/useProfessionalAccountStatus";
import useRequestPayment, {
  PaymentLine,
  linesTotal
} from "../../hooks/useRequestPayment";
import { fetchUtente } from "../../services/user";
import { useUserState } from "../../state/user";
import { extractEmails, isValidEmail } from "../../utils/email";
import { extractPatientFiscalId, normalizePatient } from "../../utils/patient";
import { FISCAL_ID_OPTIONS, PAYMENTS_HELP_CONTENT } from "../../constants";
import { formColumnSx } from "../../utils/fieldStyles";
import { CONFIGURATIONS_PATH } from "../../utils/paymentsRoutes";
import {
  centeredActionsSx,
  pageHeaderRowSx,
  pageTitleSx
} from "../../utils/pageStyles";

/** The form's footer keeps a gap between the two buttons. */
const formActionsSx = { ...centeredActionsSx, gap: 2 };

export default function RequestPayment() {
  const { t } = useTranslation();
  const history = useHistory();
  const { patientId } = useParams<{ patientId: string }>();
  const location = useLocation<{ from?: string } | undefined>();

  /**
   * Where "back" and "close" go.
   *
   * The patients list opens this screen from a card held in the query string
   * (`/patients?patient=<id>`), so it hands over that URL and we return to it —
   * closing the screen puts the card back exactly as it was. The fallback is
   * for a direct link, where there is no card to restore.
   */
  const returnTo = location.state?.from ?? `/patients?patient=${patientId}`;
  const { token } = useUserState();
  const { occupation } = useProfessionalAccountStatus();
  const {
    chargeableConfigurations,
    loading: loadingConfigurations
  } = usePaymentConfigurations();
  const { request, loading: submitting } = useRequestPayment();

  const [patient, setPatient] = useState<any>(null);
  const [loadingPatient, setLoadingPatient] = useState(true);
  const [configId, setConfigId] = useState("");
  const [fiscalOption, setFiscalOption] = useState<number | "">("");
  const [email, setEmail] = useState("");
  const [lines, setLines] = useState<PaymentLine[]>([]);
  const [addingLine, setAddingLine] = useState(false);
  const [lineToRemove, setLineToRemove] = useState<PaymentLine | null>(null);
  const [feedback, setFeedback] = useState<"success" | "error" | null>(null);

  useEffect(() => {
    if (!token || !patientId) return;
    let active = true;
    (async () => {
      try {
        // The clinical file the professional has access to for this patient.
        // The access list (`/professionals/clinicalFilesAccesses`) would do for
        // the name, but only this payload carries the tax number and email.
        const found = normalizePatient(await fetchUtente(token, patientId));
        if (!active) return;
        setPatient(found);
        setEmail(found ? extractEmails(found)[0] ?? "" : "");
      } catch {
        // Leaves `patient` null, which blocks the form below. Without this the
        // request could go out with an empty customer name.
        if (active) setPatient(null);
      } finally {
        if (active) setLoadingPatient(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token, patientId]);

  // With a single eligible account there is nothing to choose.
  useEffect(() => {
    if (!configId && chargeableConfigurations.length === 1) {
      setConfigId(chargeableConfigurations[0].id);
    }
  }, [chargeableConfigurations, configId]);

  const configuration = useMemo(
    () => chargeableConfigurations.find(item => item.id === configId),
    [chargeableConfigurations, configId]
  );

  const patientFiscalId = extractPatientFiscalId(patient);

  /**
   * The patient's own tax number is offered when known, alongside the final
   * consumer option. The mobile app also offers "register a tax number", which
   * opens its patient editing screen — there is no web equivalent, so it is
   * left out rather than shown as a dead end.
   */
  const fiscalOptions = useMemo(() => {
    const options: { label: string; value: number }[] = [];
    if (patientFiscalId) {
      options.push({
        label: patientFiscalId,
        value: Number.parseInt(patientFiscalId, 10)
      });
    }
    options.push({
      label: t("requestPayment.finalConsumer"),
      value: FISCAL_ID_OPTIONS.FINAL_CONSUMER
    });
    return options;
  }, [patientFiscalId, t]);

  const total = useMemo(() => linesTotal(lines), [lines]);
  const emailValid = isValidEmail(email);
  const canSubmit =
    lines.length > 0 && Boolean(configuration) && emailValid && !submitting;

  const onSubmit = useCallback(async () => {
    if (!configuration || !patient) return;
    setFeedback(null);
    try {
      await request({
        config: configuration,
        // The transaction's `receiver` is the clinical file id, not the nhsId
        // this route is keyed by. The mobile sends `watermelonPatient.id`, which
        // is the clinical file too — sending the nhsId here would file the
        // transaction under an id the mobile never looks it up by.
        patientId: patient.id,
        patientName: patient?.name ?? "",
        email,
        // A negative sentinel means no real tax number was picked, which is
        // what makes the request fall back to the final consumer one.
        fiscalNumber:
          typeof fiscalOption === "number" && fiscalOption > 0
            ? String(fiscalOption)
            : undefined,
        lines
      });
      setFeedback("success");
    } catch {
      setFeedback("error");
    }
  }, [configuration, request, patient, email, fiscalOption, lines]);

  if (loadingConfigurations || loadingPatient) {
    return (
      <ContainerWrapper>
        <Box display="flex" justifyContent="center" alignItems="center" mt={10}>
          <Loading />
        </Box>
      </ContainerWrapper>
    );
  }

  // A payment request carries the customer's name, so it cannot be issued
  // without knowing who the patient is.
  if (!patient) {
    return (
      <RequestPaymentMessage
        message={t("requestPayment.patientNotFound")}
        actionLabel={t("paymentConfigurations.back")}
        onAction={() => history.push("/patients")}
        variant="secondary"
      />
    );
  }

  // Nothing can be charged until an account is fully approved.
  if (chargeableConfigurations.length === 0) {
    return (
      <RequestPaymentMessage
        message={t("requestPayment.noEligibleConfigurations")}
        actionLabel={t("requestPayment.setUpAccount")}
        onAction={() => history.push(CONFIGURATIONS_PATH)}
      />
    );
  }

  return (
    <ContainerWrapper>
      <Box sx={formColumnSx}>
        <Box sx={pageHeaderRowSx}>
          <Typography sx={pageTitleSx}>{t("requestPayment.title")}</Typography>
          <HelpButton linkName={PAYMENTS_HELP_CONTENT.CREATE_PAYMENT} />
        </Box>

        <PaymentDetailsSection
          configurations={chargeableConfigurations}
          configId={configId}
          onConfigIdChange={setConfigId}
          configuration={configuration}
          fiscalOptions={fiscalOptions}
          fiscalOption={fiscalOption}
          onFiscalOptionChange={setFiscalOption}
          email={email}
          onEmailChange={setEmail}
          emailValid={emailValid}
        />

        <PaymentLinesSection
          lines={lines}
          total={total}
          onAddLine={() => setAddingLine(true)}
          onRemoveLine={setLineToRemove}
        />

        {/* On failure the form stays put so the request can be retried; the
            success case is a dialog that leaves the form on dismissal. */}
        {feedback === "error" && (
          <Alert severity="error">
            <AlertTitle>{t("requestPayment.errorTitle")}</AlertTitle>
            {t("requestPayment.errorMessage")}
          </Alert>
        )}

        <Box sx={formActionsSx}>
          <ActionButton
            label={t("paymentConfigurations.back")}
            onClick={() => history.push(returnTo)}
            variant="secondary"
          />
          <ActionButton
            label={t("requestPayment.submit")}
            onClick={onSubmit}
            loading={submitting}
            disabled={!canSubmit}
          />
        </Box>
      </Box>

      <PaymentRequestedDialog
        open={feedback === "success"}
        onDismiss={() => history.push(returnTo)}
      />

      <AddPaymentLineDialog
        open={addingLine}
        occupation={occupation}
        onClose={() => setAddingLine(false)}
        onAdd={line => setLines(current => [...current, line])}
      />

      <RemoveLineDialog
        line={lineToRemove}
        onCancel={() => setLineToRemove(null)}
        onConfirm={line => {
          setLines(current => current.filter(item => item.id !== line.id));
          setLineToRemove(null);
        }}
      />
    </ContainerWrapper>
  );
}
